import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useQuickAdd } from '../../context/QuickAddServiceContext';

interface SearchResult {
  id: string;
  title: string;
  description?: string;
  path: string;
  icon: React.ReactNode;
  category: string;
  keywords: string[];
  requiredPermission?: string;
  /** Si existe, se ejecuta en vez de navegar a `path` */
  action?: () => void;
}

const CommandSearch: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { openQuickAdd } = useQuickAdd();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  // Define all searchable pages with their metadata
  const getAllPages = (): SearchResult[] => {
    const pages: SearchResult[] = [
      // Main pages
      {
        id: 'dashboard',
        title: t('navigation.dashboard'),
        description: t('dashboard.description'),
        path: '/',
        category: t('common.main'),
        keywords: ['dashboard', 'inicio', 'home', 'panel', 'resumen', 'summary'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'calendar',
        title: t('navigation.calendar'),
        description: t('calendar.calendarDescription'),
        path: '/calendar',
        category: t('common.main'),
        keywords: ['calendar', 'calendario', 'schedule', 'agenda', 'fechas', 'dates', 'eventos', 'events'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Services
      {
        id: 'services',
        title: t('navigation.services'),
        description: t('services.title'),
        path: '/servicios',
        category: t('navigation.services'),
        keywords: ['services', 'servicios', 'tours', 'visitas', 'guiadas', 'guided'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'new-service',
        title: t('services.newService'),
        description: t('services.createNewService'),
        path: '/servicios',
        action: () => openQuickAdd(),
        category: t('navigation.services'),
        keywords: ['new service', 'nuevo servicio', 'crear', 'create', 'añadir', 'add', 'tour'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 4v16m8-8H4"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Invoicing
      {
        id: 'invoices-pending',
        title: t('navigation.servicesToBill'),
        description: 'Servicios pendientes de facturar',
        path: '/facturacion/pendientes',
        category: "Finanzas",
        keywords: ['pending services', 'servicios pendientes', 'facturar', 'bill', 'cobrar', 'sin facturar'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'invoices',
        title: t('navigation.viewInvoices'),
        description: t('invoicing.title'),
        path: '/facturacion/historial',
        category: "Finanzas",
        keywords: ['invoices', 'facturas', 'billing', 'facturación', 'cobros', 'payments', 'historial', 'history'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 14l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'new-invoice',
        title: t('invoicing.newInvoice'),
        description: t('navigation.createInvoice'),
        path: '/facturacion/new',
        category: "Finanzas",
        keywords: ['new invoice', 'nueva factura', 'crear factura', 'create invoice', 'bill', 'cobrar'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 4v16m8-8H4"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'close-quarter',
        title: t('navigation.closeQuarter'),
        description: 'Genera resumen trimestral con Excel y PDFs',
        path: '/facturacion/cierre-trimestre',
        category: "Finanzas",
        keywords: ['close quarter', 'cerrar trimestre', 'resumen trimestral', 'reportes', 'reports', 'excel', 'trimestre'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 17V7m0 10l3-3m-3 3l-3-3m13 3V7m0 10l3-3m-3 3l-3-3M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'invoice-templates',
        title: t('invoicing.templates'),
        description: 'Gestiona las plantillas de facturas',
        path: '/facturacion/templates',
        category: "Finanzas",
        keywords: ['templates', 'plantillas', 'invoice templates', 'diseño', 'design', 'personalizar'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Clients
      {
        id: 'clients',
        title: t('navigation.clients'),
        description: t('clients.management'),
        path: '/clientes',
        category: t('navigation.clients'),
        keywords: ['clients', 'clientes', 'customers', 'agencias', 'agencies', 'contactos'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'new-client',
        title: t('clients.newClient'),
        description: 'Añade un nuevo cliente',
        path: '/clientes/nuevo',
        category: t('navigation.clients'),
        keywords: ['new client', 'nuevo cliente', 'añadir cliente', 'add client', 'crear', 'create'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 4v16m8-8H4"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Files
      {
        id: 'files',
        title: t('navigation.files'),
        description: t('files.title'),
        path: '/archivos',
        category: t('common.main'),
        keywords: ['files', 'archivos', 'documents', 'documentos', 'upload', 'subir', 'descargar'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Tools
      {
        id: 'rates',
        title: t('navigation.rates'),
        description: t('rates.title'),
        path: '/tarifas',
        category: t('navigation.tools'),
        keywords: ['rates', 'tarifas', 'precios', 'prices', 'costos', 'costs', 'pricing'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'referrals',
        title: t('navigation.referrals'),
        description: 'Programa de referidos',
        path: '/referidos',
        category: t('navigation.tools'),
        keywords: ['referrals', 'referidos', 'recomendaciones', 'recommendations', 'programa', 'program'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Configuration
      {
        id: 'config',
        title: t('navigation.dataConfiguration'),
        description: 'Configuración de datos maestros',
        path: '/configuracion',
        category: t('navigation.tools'),
        keywords: ['configuration', 'configuración', 'settings', 'ajustes', 'datos', 'data'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
        requiredPermission: 'guia'
      },
      
      // User Account
      {
        id: 'profile',
        title: t('navigation.myProfile'),
        description: t('profile.description'),
        path: '/profile',
        category: t('common.account'),
        keywords: ['profile', 'perfil', 'account', 'cuenta', 'personal', 'información'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'notifications',
        title: t('navigation.notifications'),
        description: t('notifications.title'),
        path: '/notifications',
        category: t('common.account'),
        keywords: ['notifications', 'notificaciones', 'alerts', 'alertas', 'avisos', 'messages'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'settings',
        title: t('dashboard.settings'),
        description: t('settings.description'),
        path: '/settings',
        category: t('common.account'),
        keywords: ['settings', 'ajustes', 'preferences', 'preferencias', 'configuración', 'configuration'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'subscription',
        title: t('navigation.subscriptionBilling'),
        description: 'Gestiona tu suscripción',
        path: '/suscripcion',
        category: t('common.account'),
        keywords: ['subscription', 'suscripción', 'billing', 'facturación', 'plan', 'pago'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Support
      {
        id: 'help-center',
        title: t('navigation.helpCenter'),
        description: 'Centro de ayuda y documentación',
        path: '/support/help-center',
        category: t('navigation.helpSupport'),
        keywords: ['help', 'ayuda', 'support', 'soporte', 'documentation', 'documentación'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'ai-assistant',
        title: t('navigation.aiAssistant'),
        description: 'Chat con el asistente de IA',
        path: '/support/chat',
        category: t('navigation.helpSupport'),
        keywords: ['ai', 'ia', 'assistant', 'asistente', 'chat', 'bot', 'ayuda'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>,
        requiredPermission: 'guia'
      },
      
    ];

    // Filter pages based on user permissions
    return pages.filter(page => {
      if (!page.requiredPermission) return true;
      if (!user) return false;
      
      if (user.permisos === 'superadmin') return true;
      if (page.requiredPermission === 'admin' && user.permisos === 'admin') return true;
      if (page.requiredPermission === 'guia' && (user.permisos === 'guia' || user.permisos === 'admin')) return true;
      
      return false;
    });
  };

  // Filter results based on search query
  const filteredResults = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase();
    const allPages = getAllPages();
    
    return allPages.filter(page => {
      // Search in title, description and keywords
      const inTitle = page.title.toLowerCase().includes(query);
      const inDescription = page.description?.toLowerCase().includes(query) || false;
      const inKeywords = page.keywords.some(keyword => keyword.toLowerCase().includes(query));
      
      return inTitle || inDescription || inKeywords;
    }).sort((a, b) => {
      // Prioritize exact matches in title
      const aExact = a.title.toLowerCase() === query;
      const bExact = b.title.toLowerCase() === query;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      // Then prioritize title matches
      const aTitle = a.title.toLowerCase().includes(query);
      const bTitle = b.title.toLowerCase().includes(query);
      if (aTitle && !bTitle) return -1;
      if (!aTitle && bTitle) return 1;
      
      return 0;
    });
  }, [searchQuery, i18n.language, t, user]);

  // Group results by category
  const groupedResults = React.useMemo(() => {
    const groups: Record<string, SearchResult[]> = {};
    
    filteredResults.forEach(result => {
      if (!groups[result.category]) {
        groups[result.category] = [];
      }
      groups[result.category].push(result);
    });
    
    return groups;
  }, [filteredResults]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % filteredResults.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + filteredResults.length) % filteredResults.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredResults[selectedIndex]) {
            runResult(filteredResults[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          handleClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredResults, navigate]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleOpen = () => {
    setIsOpen(true);
    setSelectedIndex(0);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
    setSelectedIndex(0);
  };

  const runResult = (result: SearchResult) => {
    handleClose();
    if (result.action) return result.action();
    navigate(result.path);
  };

  return (
    <div ref={searchRef} className="relative">
      <div className="relative">
        <span className="absolute -translate-y-1/2 pointer-events-none left-4 top-1/2">
          <svg
            className="fill-gray-500 dark:fill-gray-400"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
              fill=""
            />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={handleOpen}
          placeholder="Buscar páginas y funciones..."
          className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 xl:w-[430px]"
        />
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (searchQuery.trim() || filteredResults.length > 0) && (
        <div className="absolute top-full mt-2 w-full xl:w-[430px] bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden z-50">
          {filteredResults.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              {searchQuery.trim() ? t('common.noResults') : t('common.startTyping')}
            </div>
          ) : filteredResults.length === 0 && !searchQuery.trim() ? (
            <div className="p-4">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                Sugerencias
              </div>
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <div>• Busca "servicios" para gestionar tus visitas guiadas</div>
                <div>• Escribe "nueva factura" para crear una factura</div>
                <div>• Prueba "configuración" para ajustes del sistema</div>
              </div>
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              {Object.entries(groupedResults).map(([category, results]) => (
                <div key={category}>
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50">
                    {category}
                  </div>
                  {results.map((result, index) => {
                    const globalIndex = filteredResults.indexOf(result);
                    return (
                      <button
                        key={result.id}
                        onClick={() => runResult(result)}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                        className={`w-full px-3 py-2 flex items-center space-x-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                          selectedIndex === globalIndex ? 'bg-gray-100 dark:bg-gray-700' : ''
                        }`}
                      >
                        <div className="flex-shrink-0 text-gray-400 dark:text-gray-500">
                          {result.icon}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {result.title}
                          </div>
                          {result.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {result.description}
                            </div>
                          )}
                        </div>
                        {selectedIndex === globalIndex && (
                          <div className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
                            ↵
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommandSearch;